import React, { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { CDN_TYPE_SPORT, getCDNUrl } from 'utils';

import {
  AuthApi,
  printMissingConfigError,
  SettingsManager,
  SiteConfigManager,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { Flag, Icon } from '@atlas/ui';

import { cmdSymbol, languageTypeFlag, languageTypeISO } from 'common/constants';

import LangSwitchSmallCover from './LangSwitchSmallCover';

const LangSwitch = ({ isSmall, onlyFooter, search }) => {
  const settings = useSettings();

  const { lang } = settings;
  const languages = SiteConfigManager.getConfig('languages', []);
  const mobileLanguageType = SiteConfigManager.getConfig(
    'config.config.language.mobileLanguageType',
  );

  const isLanguageTypeFlag = mobileLanguageType === languageTypeFlag;
  const isLanguageTypeISO = mobileLanguageType === languageTypeISO;

  const { account, authenticated } = useApplicationState();
  const isCmdLink = search.indexOf(cmdSymbol) !== -1;

  const changeHandler = useCallback(
    option => {
      const newLang = option.target.value;

      SettingsManager.updateSettings({ lang: newLang });

      const currentLang = account?.language;

      if (authenticated && newLang !== currentLang) {
        AuthApi.updateUserLanguage({ language: newLang });
      }
    },
    [authenticated, account],
  );

  const selectContainerClass = cx(
    'select-field-container lang',
    { 'only-flag': isSmall && isLanguageTypeFlag },
    { 'only-code': isSmall && !isLanguageTypeFlag },
  );

  const getCurrentLangCode = languages.find(({ code }) => code === lang);

  if (languages.length === 0) {
    printMissingConfigError('languages');
  }

  if (languages.length <= 1) {
    return null;
  }

  return (
    <div className={selectContainerClass}>
      <div className="lang-select">
        {onlyFooter && getCurrentLangCode ? (
          <Flag
            alt={getCurrentLangCode?.name}
            cdnHostname={getCDNUrl(CDN_TYPE_SPORT)}
            name={getCurrentLangCode?.regionId}
          />
        ) : (
          <LangSwitchSmallCover
            getCurrentRegionId={getCurrentLangCode?.regionId}
            getCurrentRegionName={getCurrentLangCode?.name}
            isLanguageTypeFlag={isLanguageTypeFlag}
            isLanguageTypeISO={isLanguageTypeISO}
            isSmall={isSmall}
            lang={lang}
          />
        )}

        <select
          className="select-field"
          disabled={!languages.length || isCmdLink}
          onChange={changeHandler}
          value={lang}
        >
          {languages.map(({ code, name }) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {(!isSmall || !isLanguageTypeFlag) && (
        <Icon className="icon-container icon-arrow-down" name="arrow-down" />
      )}
    </div>
  );
};

export const LangSwitchWithoutRouter = ({ isSmall, onlyFooter }) => {
  const { search } = window.location;
  return <LangSwitch isSmall={isSmall} onlyFooter={onlyFooter} search={search} />;
};

const LangSwitchWithRouter = ({ isSmall, onlyFooter }) => {
  const { search } = useLocation();
  return <LangSwitch isSmall={isSmall} onlyFooter={onlyFooter} search={search} />;
};

LangSwitch.propTypes = { isSmall: PropTypes.bool };

export default LangSwitchWithRouter;
